import { AssetCategory, ClassesAsset } from 'common/interfaces';
import { merge } from 'lodash';
import { action, observable, computed } from 'mobx';

import { RootStore } from 'stores/rootStore';
import { API_FUNCTION_TYPE_IMP, API_ACCOUNT_IMP, API_ACCOUNT_DETAIL_IMP } from 'api/endpoints';
import { PieSeriesData } from 'components/Graph/PieGraph';
import { BucketPieDataset, PortfolioType } from './model/interfaces';
import { convertPiechartData } from 'helpers/chart';
import { BucketType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';

import { fingerprint } from 'helpers/helpers';

interface PortfolioData {
  assetCategories: AssetCategory[];
  availableAssets: ClassesAsset[];
  bucketProfile: string;
  calculatedProfile: string;
  minProfile: string;
  maxProfile: string;
  terAverage: number;
  esgScore: number;
  title?: string;
  chart: {
    dataset: BucketPieDataset[];
  };
  reservedCashCode: string;
}

interface FormValue {
  code: string;
  assetClasses: {
    assets?: {
      code: string;
      share: number;
      maxShare?: number;
      varianceClass: string;
      isin: string;
    }[];
  }[];
}

//export const MAX_SHARE = 98;

export class PortfolioEditorStore {
  private rootStore: RootStore;
  private accountId: string;
  private bucket: BucketType;
  private endpoint: API_FUNCTION_TYPE_IMP;
  private currentValues: FormValue[] = [];
  private originalPortfolioSaved = false;
  private originalPortfolio = '';
  private newPortfolio = '';

  title?: string;

  @observable data: PortfolioData | null = null;
  @observable sum: number | null = null;
  @observable usedCodes: string[] = [];
  @observable editing = false;
  @observable processing = false;
  @observable valid = true;
  @observable profileValid = true;
  @observable assetUnderLimit = false;
  @observable assetOverLimit = false;
  @observable maxShare = 0;

  @observable selectedAssets: AssetCategory[] = [];

  constructor(rootStore: RootStore, accountId: string, detailMode: boolean, bucket: BucketType) {
    this.rootStore = rootStore;
    this.accountId = accountId;
    this.bucket = bucket;
    this.endpoint = detailMode ? API_ACCOUNT_DETAIL_IMP : API_ACCOUNT_IMP;
  }

  @action setValid = (valid: boolean) => (this.valid = valid);
  @action setEditing = (edit: boolean) => (this.editing = edit);
  @action setProcessing = (processing: boolean) => (this.processing = processing);
  setTitle = (title: string) => (this.title = title);

  fingerprintsMatch = () => {
    return this.originalPortfolio === this.newPortfolio;
  };

  @action
  fetchData = async () => {
    const response = await this.rootStore.stores.apiStore.get<PortfolioData>(
      this.endpoint(this.accountId, this.bucket),
    );

    if (response?.data) {
      response.data.availableAssets = response.data.availableAssets.map((a) => {
        return { ...a, id: a.code };
      });
      this.data = response.data;
      this.calculateMaxShare();
      if (!this.originalPortfolioSaved) {
        this.originalPortfolio = fingerprint(this.data);
        this.newPortfolio = fingerprint(this.data);
        this.originalPortfolioSaved = true;
      }
      this.selectedAssets = response.data.assetCategories;
      this.check(this.selectedAssets, true);
    }
  };

  @action
  calculateMaxShare = () => {
    this.data?.assetCategories.forEach((assetCategory) => {
      assetCategory.assetClasses.forEach((assetClass) => {
        assetClass.assets?.forEach((asset) => {
          if (asset.code === this.data!.reservedCashCode) this.maxShare = 100 - asset.share * 100;
          if (this.maxShare) return;
        });
        if (this.maxShare) return;
      });
      if (this.maxShare) return;
    });
  };

  @action
  removeFromSelectedAssets = (categoryIndex: number, classIndex: number, assetIndex: number) => {
    this.selectedAssets[categoryIndex].assetClasses[classIndex].assets?.splice(assetIndex, 1);
    this.currentValues[categoryIndex]!.assetClasses[classIndex]!.assets?.splice(assetIndex, 1);

    if (!this.selectedAssets[categoryIndex].assetClasses[classIndex].assets?.length) {
      this.selectedAssets[categoryIndex].assetClasses.splice(classIndex, 1);
      this.currentValues[categoryIndex].assetClasses.splice(classIndex, 1);
    }

    if (!this.selectedAssets[categoryIndex].assetClasses.length) {
      this.selectedAssets.splice(categoryIndex, 1);
      this.currentValues.splice(categoryIndex, 1);
    }

    this.selectedAssets = merge({}, { selected: this.selectedAssets }, { selected: this.currentValues }).selected;
    this.setEditing(true);
    this.valid = false;
  };

  @action
  addToSelectedAssets = (asset: ClassesAsset) => {
    let categoryIndex = this.selectedAssets.findIndex((c) => c.code === asset.assetCategory!);
    if (categoryIndex === -1) {
      this.selectedAssets.push({ code: asset.assetCategory!, title: asset.assetCategory!, share: 0, assetClasses: [] });
      this.currentValues.push({ code: asset.assetCategory!, assetClasses: [] });
      categoryIndex = this.selectedAssets.length - 1;
    }

    let classIndex = this.selectedAssets[categoryIndex].assetClasses.findIndex((c) => (c.code = asset.assetClass!));
    if (classIndex === -1) {
      this.selectedAssets[categoryIndex].assetClasses.push({
        code: asset.assetClass!,
        title: asset.assetClass!,
        share: 0,
        assets: [],
      });
      this.currentValues[categoryIndex].assetClasses.push({ assets: [] });
      classIndex = this.selectedAssets[categoryIndex].assetClasses.length - 1;
    }

    this.selectedAssets[categoryIndex].assetClasses[classIndex].assets!.push({ ...asset, share: 0.01 });
    this.currentValues[categoryIndex].assetClasses[classIndex].assets!.push({
      code: asset.code,
      varianceClass: asset.varianceClass,
      maxShare: asset.maxShare,
      share: 0.01,
      isin: asset.isin,
    });

    this.usedCodes.push(asset.code);

    this.selectedAssets = merge({}, { selected: this.selectedAssets }, { selected: this.currentValues }).selected;
    this.selectedAssets = this.selectedAssets.slice().sort((a, b) => a.code.localeCompare(b.code));
    this.currentValues = this.currentValues.slice().sort((a: any, b: any) => a.code.localeCompare(b.code));

    this.setEditing(true);
    this.valid = false;
  };

  convertPortfolioData = (data: AssetCategory[], percentage?: boolean, raw?: boolean) => {
    return data.map((assetCategory) => {
      return {
        code: assetCategory.code,
        assetClasses: assetCategory.assetClasses.map((assetClass) => {
          return {
            assets: assetClass.assets?.map((asset) => {
              return {
                code: asset.code,
                varianceClass: asset.varianceClass,
                maxShare: raw ? asset.maxShare : percentage ? (asset.maxShare || 0) * 100 : (asset.maxShare || 0) / 100,
                share: raw ? asset.share : percentage ? asset.share * 100 : asset.share / 100,
                isin: asset.isin,
              };
            }),
          };
        }),
      };
    });
  };

  @action
  check = (data?: AssetCategory[], raw?: boolean) => {
    if (!data) {
      data = this.selectedAssets;
      raw = true;
    }
    let sum = 0;
    let codes: string[] = [];
    let validation = true;
    this.assetUnderLimit = false;
    this.assetOverLimit = false;
    for (const assetCategory of data) {
      for (const assetClass of assetCategory.assetClasses) {
        for (const asset of assetClass.assets || []) {
          const share = Number(asset.share);
          sum += share;
          raw && codes.push(asset.code);
          if (!raw && (share < 0.1 || share > this.maxShare)) {
            validation = false;
            if (share < 0.1) this.assetUnderLimit = true;
            if (share > this.maxShare) this.assetOverLimit = true;
          }
        }
      }
    }
    this.sum = Math.round(sum * 10000) / (raw ? 100 : 10000);
    if (this.sum !== 100) validation = false;
    if (raw) this.usedCodes = codes;
    const cP = Number(this.data?.calculatedProfile);

    if (this.data?.minProfile && this.data?.maxProfile) {
      this.profileValid = !(cP < Number(this.data.minProfile) || cP > Number(this.data.maxProfile));
    }

    this.valid = validation;
    this.currentValues = this.convertPortfolioData(data, false, raw);
  };

  @action
  process = async () => {
    if (!this.valid) return;

    this.setProcessing(true);
    const response = await this.rootStore.stores.apiStore.patch<PortfolioData>(
      this.endpoint(this.accountId, this.bucket),
      {
        title: this.title,
        minProfile: this.data?.minProfile,
        maxProfile: this.data?.maxProfile,
        assetCategories: this.currentValues,
      },
      true,
    );
    if (response?.data && response?.status === 200) {
      this.data = response.data;
      this.calculateMaxShare();
      this.newPortfolio = fingerprint(this.data);
      this.selectedAssets = [];
      this.currentValues = [];
      this.selectedAssets = response.data.assetCategories;
      this.check(this.selectedAssets, true);
    } else {
      this.valid = false;
    }
    this.setEditing(false);
    this.setProcessing(false);
  };

  save = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<{ value: boolean }>(
      this.endpoint(this.accountId, this.bucket),
      {
        title: this.title,
        minProfile: this.data?.minProfile,
        maxProfile: this.data?.maxProfile,
        assetCategories: this.currentValues,
      },
      true,
    );
    this.rootStore.stores.uiStore.setLoading(false);
    return !!response?.data?.value;
  };

  @computed
  get pie(): PieSeriesData {
    if (!this.data) return {};
    return convertPiechartData(
      this.data.chart?.dataset,
      { code: 'IND', type: PortfolioType.IND, profile: '0' },
      this.bucket,
    );
  }

  filterETFs = (category: string) => {
    return (
      this.data?.availableAssets?.filter((d) => d.assetCategory! === category && !this.usedCodes.includes(d.code)) || []
    );
  };
}
